import type {
  DashboardOverview,
  DashboardRange,
  PaperOverview,
  PaperPositionDetail,
  PaperPositionSummary,
  PerformancePoint,
  ProcessHeartbeat,
  ShadowSimulation,
  TradeDetail,
  TradeSummary,
} from "../../src/dashboard/types.js";

export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(`/bot/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as {
        error?: { message?: string };
      };
      message = body.error?.message ?? message;
    } catch {
      // The status remains the stable fallback for non-JSON proxy errors.
    }
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export const dashboardApi = {
  session: () => request<{ authenticated: true; username: string }>("/session"),
  login: (username: string, password: string) =>
    request<{ authenticated: true }>("/session", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () =>
    request<{ authenticated: false }>("/session", { method: "DELETE" }),
  overview: (range: DashboardRange) =>
    request<DashboardOverview>(`/overview?range=${range}`),
  performance: (range: DashboardRange) =>
    request<{ items: PerformancePoint[] }>(`/performance?range=${range}`),
  positions: (filters: { state: string; mint: string }) => {
    const query = new URLSearchParams({ limit: "100" });
    if (filters.state) query.set("state", filters.state);
    if (filters.mint) query.set("mint", filters.mint);
    return request<{ items: TradeSummary[]; next_cursor: string | null }>(
      `/positions?${query.toString()}`,
    );
  },
  position: (id: string) => request<TradeDetail>(`/positions/${id}`),
  simulations: () =>
    request<{ items: ShadowSimulation[] }>("/simulations?limit=100"),
  paperOverview: () => request<PaperOverview>("/paper/overview"),
  paperPerformance: (range: DashboardRange) =>
    request<{ items: PerformancePoint[] }>(`/paper/performance?range=${range}`),
  paperPositions: (filters: { state: string; mint: string }) => {
    const query = new URLSearchParams({ limit: "100" });
    if (filters.state) query.set("state", filters.state);
    if (filters.mint) query.set("mint", filters.mint);
    return request<{
      items: PaperPositionSummary[];
      next_cursor: string | null;
    }>(`/paper/positions?${query.toString()}`);
  },
  paperPosition: (id: string) =>
    request<PaperPositionDetail>(`/paper/positions/${id}`),
  operations: () => request<{ heartbeats: ProcessHeartbeat[] }>("/operations"),
};
